import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingCart, Trash2, ArrowRight } from 'lucide-react';
import { useWishlist } from '../context/WishlistContext';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export const WishlistPage = () => {
  const { items, removeItem, clearWishlist } = useWishlist();
  const { addItem } = useCart();

  const handleAddToCart = (product) => {
    if (product.stock > 0) {
      addItem(product, 1);
      toast.success(`${product.name} added to cart!`);
    } else {
      toast.error('This product is out of stock');
    }
  };

  const handleMoveAllToCart = () => {
    let addedCount = 0;
    items.forEach(product => {
      if (product.stock > 0) {
        addItem(product, 1);
        addedCount++;
      }
    });
    if (addedCount > 0) {
      toast.success(`${addedCount} item${addedCount > 1 ? 's' : ''} added to cart!`);
    }
  };

  const formatCategory = (category) => {
    return category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div data-testid="wishlist-page" className="min-h-screen pt-28 pb-16 bg-cream">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-terracotta font-mono text-sm uppercase tracking-wider">
            Your Favorites
          </span>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-forest mt-2 mb-4">
            Wishlist
          </h1>
          <p className="text-warm-brown text-lg">
            {items.length} item{items.length !== 1 ? 's' : ''} saved
          </p>
        </div>

        {items.length === 0 ? (
          <div className="text-center py-16">
            <Heart className="h-16 w-16 text-gray-300 mx-auto mb-6" />
            <h2 className="font-heading text-2xl text-forest mb-4">Your wishlist is empty</h2>
            <p className="text-gray-500 mb-8">
              Start adding products you love to your wishlist!
            </p>
            <Link to="/shop">
              <Button className="bg-forest text-white rounded-full px-8">
                Browse Products
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        ) : (
          <>
            {/* Actions */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <Button
                onClick={handleMoveAllToCart}
                className="bg-forest text-white rounded-full"
                data-testid="add-all-to-cart"
              >
                <ShoppingCart className="h-5 w-5 mr-2" />
                Add All to Cart
              </Button>
              <Button
                onClick={clearWishlist}
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50 rounded-full"
                data-testid="clear-wishlist"
              >
                <Trash2 className="h-5 w-5 mr-2" />
                Clear Wishlist
              </Button>
            </div>

            {/* Wishlist Items */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((product) => (
                <div
                  key={product.id}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`wishlist-item-${product.id}`}
                >
                  <Link to={`/product/${product.id}`}>
                    <div className="aspect-square overflow-hidden">
                      <img
                        src={product.images?.[0] || 'https://images.unsplash.com/photo-1672736810868-130c00ef5a9e?w=600&q=80'}
                        alt={product.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1672736810868-130c00ef5a9e?w=600&q=80'; }}
                      />
                    </div>
                  </Link>
                  
                  <div className="p-5">
                    <span className="text-xs font-mono uppercase tracking-wider text-warm-brown">
                      {formatCategory(product.category)}
                    </span>
                    <Link to={`/product/${product.id}`}>
                      <h3 className="font-heading font-semibold text-lg text-forest mt-1 hover:text-gold transition-colors">
                        {product.name}
                      </h3>
                    </Link>
                    
                    <div className="flex items-center justify-between mt-4">
                      <span className="text-xl font-bold text-forest">
                        ${product.price?.toFixed(2)}
                      </span>
                      <span className={`text-sm ${product.stock > 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {product.stock > 0 ? `${product.stock} in stock` : 'Out of stock'}
                      </span>
                    </div>
                    
                    <div className="flex gap-2 mt-4">
                      <Button
                        onClick={() => handleAddToCart(product)}
                        disabled={product.stock === 0}
                        className="flex-1 bg-forest hover:bg-forest-light text-white rounded-full"
                        data-testid={`add-to-cart-${product.id}`}
                      >
                        <ShoppingCart className="h-4 w-4 mr-2" />
                        Add to Cart
                      </Button>
                      <Button
                        onClick={() => removeItem(product.id)}
                        variant="outline"
                        className="rounded-full border-red-300 text-red-500 hover:bg-red-50"
                        data-testid={`remove-wishlist-${product.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
